import {useEffect, useRef} from 'react';
import styled from 'styled-components';
import {Footer as AIChatFooter, ButtonIcon} from 'pepsico-ds';

interface AiChatFooterHocProps {
  autocompleteItems?: string[];
  onSendMessage?: (message: string) => void;
  onSuggestionClick?: (suggestion: string) => void;
  chatbotTitle?: string;
  isLoading?: boolean;
  onStop?: React.MouseEventHandler<HTMLDivElement>;
}

const FooterContainer = styled.div<{$isLoading?: boolean}>`
  position: relative;
  width: 100%;

  /* Hide the pepsico-ds send icon (and any other inline action) while a
     response is streaming so only the overlay Stop button is visible. */
  ${({$isLoading}) =>
    $isLoading &&
    `
    .footer .field button,
    .footer .field [role='button'],
    .footer .field [class*='send' i],
    .footer .field [class*='Send'],
    .footer .field [class*='action' i] {
      display: none !important;
    }
  `}
`;

const StopButtonWrapper = styled.div`
  position: absolute;
  right: clamp(0.5rem, 1vw, 0.875rem);
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const AiChatFooterHoc = ({
  autocompleteItems,
  onSendMessage,
  onSuggestionClick,
  chatbotTitle = 'AI Assistant',
  isLoading = false,
  onStop,
}: AiChatFooterHocProps) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // pepsico-ds Footer has no `disabled` prop; toggle the textarea imperatively.
  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;
    const inputs = root.querySelectorAll<
      HTMLTextAreaElement | HTMLInputElement
    >('.footer .field textarea, .footer .field input');
    inputs.forEach((el) => {
      el.disabled = isLoading;
      if (isLoading) {
        el.setAttribute('aria-busy', 'true');
      } else {
        el.removeAttribute('aria-busy');
      }
    });
  }, [isLoading]);

  return (
    <FooterContainer
      ref={wrapperRef}
      $isLoading={isLoading}
    >
      <AIChatFooter
        autocompleteItems={autocompleteItems}
        chatbotTitle={chatbotTitle}
        onSendMessage={onSendMessage}
        onSuggestionClick={onSuggestionClick}
        showAutocomplete={false}
        showAction1={false}
        showAction2={false}
        showAction3={false}
        state='Default'
      />
      {isLoading && onStop && (
        <StopButtonWrapper
          onClick={onStop}
          role='button'
          aria-label='Stop generating response'
        >
          <ButtonIcon
            type='default'
            size='medium'
            icon='stop_circle'
            label='Stop generating response'
          />
        </StopButtonWrapper>
      )}
    </FooterContainer>
  );
};

export default AiChatFooterHoc;
